import React, { useCallback, useContext, useEffect, useState } from 'react'
import { RouteChildrenProps } from 'react-router'
import { css } from '@emotion/core'
import { Form, Formik, FormikActions, FormikProps } from 'formik'
import * as Yup from 'yup'
import { useDebounce } from 'use-debounce'
import { Typography } from '@dw/helix'

import { ArticlesContext, AuthContext, UserGroup } from '../../contexts'

import { ALL_REASON_CODES, REASON_CODES } from '../ScanPage/reasonCodes'
import ScanForm from './ScanForm'
import { fetchOrderItemsByOrderNumber, OrderItem } from '../../utils/orderApi'
import { batteryChange, createTraces, fetchAllTracesByOrderNumber, Trace } from '../../utils/traceApi'
import ScanOverview from './ScanOverview'
import { isValidSerialNumberFormat, parseSerialNumbers } from './utils/serialNumbers'
import { listHasOnlyUniqueStrings } from './utils'
import flatten from '../../utils/flatten'
import useActiveElement from '../../hooks/useActiveElement'
import usePrevious from '../../hooks/usePrevious'

import {
  FEEDBACK_MESSAGE_ERROR_MAPPING,
  FEEDBACK_MESSAGE_SUCCESS_MAPPING,
  FeedbackErrorMessage,
  FeedbackMessageErrorMapping,
  FeedbackSuccessMessage,
} from './feedbackMessages'
import { ModalFormData } from './components/ManualInputModal'
import { ORDER_NUMBER_ACCESS_GROUPS, SHIP_TO_ACCESS_GROUPS } from '../ScanPage/ScanPage'

const headerStyles = css`
  margin-top: 20px;
  margin-bottom: 20px;
`

const formStyles = css`
  display: flex;
  flex-direction: row;

  > section:first-of-type {
    margin-right: 20px;
    min-width: 260px;
  }
  > section:nth-of-type(2) {
    width: 100%;
  }
`

export const panelStyles = css`
  background-color: #fff;
  padding: 12px 15px 0;
  border-radius: 2px;
  box-shadow: 0 1px 7px 0 rgba(0, 0, 0, 0.19), 0 3px 3px 0 rgba(0, 0, 0, 0.13);
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  position: relative;
  min-height: 250px;
`

const scanFormPanelStyles = css`
  ${panelStyles};
  position: sticky;
  z-index: 1;
  top: 70px;
  margin: 0 auto auto;
`

const getScanOverviewPanelStyles = (isDeleteMode: boolean) =>
  css`
    ${panelStyles};
    border: ${isDeleteMode ? '3px solid rgb(216, 0, 0)' : 'none'};
  `

interface ScanFormValues {
  orderNumber: string
  articleId: string
  traceEvent: string
  date: Date | null
  location: string
  shipTo: string
  reasonCode?: string
  specifiedReasonCode?: string
}

export interface ScanItem extends OrderItem {
  traces: Trace[] // Traces registered in previous sessions
  serialNumbers: string[] // Serial numbers added this session
  needAttention: string[] // Serial numbers that needs battery change
  isManualInput?: boolean
  Created?: string
}

export interface ScanItemMapping {
  [articleId: string]: ScanItem
}

type KeyboardListener = (callback: (scannedText: string) => any) => (event: KeyboardEvent) => void

const createKeyboardListener: KeyboardListener = callback => {
  let typedCharacters: string = ''
  let typingTimer: ReturnType<typeof setTimeout>
  return (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      return
    }

    typedCharacters += event.key
    clearTimeout(typingTimer)

    typingTimer = setTimeout(() => {
      callback(typedCharacters)
      typedCharacters = ''
    }, 100)
  }
}

const scanFormValidationSchema = ({ validateOrderNumber = false, validateShipTo = false }) => {
  const validationRules = {
    orderNumber: Yup.string(),
    articleId: Yup.string(),
    traceEvent: Yup.string().required('Trace Event is required'),
    reasonCode: Yup.string().when('traceEvent', {
      is: val => ['Discarded', 'Repaired'].includes(val),
      then: Yup.string().required('Product group is required'),
      otherwise: Yup.string().notRequired(),
    }),
    specifiedReasonCode: Yup.string().when('tracEvent', {
      is: val => ['Discarded', 'Repaired'].includes(val),
      then: Yup.string().required('Description is required'),
      otherwise: Yup.string().notRequired(),
    }),
    date: Yup.date()
      .nullable()
      .required('Trace Date is required'),
    location: Yup.string().required('Location is required'),
    shipTo: Yup.string(),
  }

  if (validateOrderNumber) {
    validationRules.orderNumber = Yup.string().required('Order Number is required')
  }

  if (validateShipTo) {
    validationRules.shipTo = Yup.string().required('Ship To is required')
  }

  return Yup.object().shape(validationRules)
}

const Scan = (props: RouteChildrenProps) => {
  const { hasGroup, userAttributes } = useContext(AuthContext)
  const { eanArticleMapping, articles } = useContext(ArticlesContext)
  const [scannedTextInState, setScannedTextInState] = useState('')
  const [selectedArticleIdInState, setSelectedArticleIdInState] = useState('')
  const [selectedOrderNumberInState, setSelectedOrderNumberInState] = useState<string>('')
  const [scanItemMappingInState, setScanItemMappingInState] = useState<ScanItemMapping>({})
  const [isScanItemMappingLoadingInState, setIsScanItemMappingLoadingInState] = useState<boolean>(false)

  const [feedbackMessagesInState, setFeedbackMessagesInState] = useState<
    (FeedbackErrorMessage | FeedbackSuccessMessage)[]
  >([])

  const [isDeleteModeInState, setIsDeleteModeInState] = useState<boolean>(false)
  const [debouncedSelectedOrderNumber] = useDebounce(selectedOrderNumberInState, 1000)
  const [debouncedSelectedArticleId] = useDebounce(selectedArticleIdInState, 1000)
  const previousDebouncedSelectedOrderNumber = usePrevious(debouncedSelectedOrderNumber)
  const activeElement: Element | null = useActiveElement()

  const { companyName, locations = [], roles = [] } = userAttributes || {}

  useEffect(() => {
    const keyboardListener = createKeyboardListener((typedCharacters: string) => {
      // We don’t set scanned text in state when order number field is focused to prevent
      // further processing of scanned text. We only want to use scanned text for eaither
      // scanned serial numbers(11 character length) or scanned EAN13(13 character length) codes.
      const activeElementName = activeElement ? activeElement.getAttribute('name') : ''
      const isActiveOrderNumberField = activeElementName === 'orderNumber'
      if (isActiveOrderNumberField || typedCharacters.length < 11) return
      setScannedTextInState(typedCharacters)
    })
    document.addEventListener('keypress', keyboardListener)
    return () => document.removeEventListener('keypress', keyboardListener)
  }, [activeElement])

  useEffect(() => {
    if (hasGroup([UserGroup.Franchise])) return
    if (!debouncedSelectedArticleId) return
    if (debouncedSelectedArticleId !== selectedArticleIdInState) return

    const scanItem = Object.values(scanItemMappingInState).find(
      ({ articleId }) => articleId === debouncedSelectedArticleId,
    )
    if (!scanItem) {
      setFeedbackMessagesInState([FEEDBACK_MESSAGE_SUCCESS_MAPPING.INFORMATION_MESSAGE_SUCCESS])
    }
  }, [debouncedSelectedArticleId, hasGroup, selectedArticleIdInState, scanItemMappingInState])

  useEffect(() => {
    if (!debouncedSelectedOrderNumber && !previousDebouncedSelectedOrderNumber) return

    if (hasGroup([UserGroup.Franchise])) {
      setScanItemMappingInState({})
      return
    }
    const getScanItemMapping = async () => {
      let scannableOrderItems: OrderItem[]
      let orderScanItemMapping: ScanItemMapping = {}
      const orderTraces: Trace[] = await fetchAllTracesByOrderNumber(debouncedSelectedOrderNumber)

      try {
        const orderItems = await fetchOrderItemsByOrderNumber(debouncedSelectedOrderNumber)
        scannableOrderItems = orderItems.filter(({ articleId }) => {
          const { scannable } = articles[articleId] || {}
          return scannable
        })
      } catch {
        scannableOrderItems = []
      }

      if (scannableOrderItems.length) {
        orderScanItemMapping = scannableOrderItems.reduce((mapping: ScanItemMapping, scannableOrderItem) => {
          const { articleId } = scannableOrderItem
          if (!mapping[articleId]) {
            const articleTraces = orderTraces.filter(({ ProductId }) => ProductId === articleId)
            const newScanItem = {
              ...scannableOrderItem,
              traces: articleTraces,
              serialNumbers: [],
              needAttention: [],
              isManualInput: false,
            }
            mapping[articleId] = newScanItem
            return mapping
          }
          const scanItem = mapping[articleId]
          const updatedQuantity = scanItem.quantity + scannableOrderItem.quantity
          const updatedScanItem = { ...scanItem, quantity: updatedQuantity }

          mapping[articleId] = updatedScanItem
          return mapping
        }, {})
      }

      const nonOrderItemTraces = orderTraces.filter(({ ProductId }) => {
        return !scannableOrderItems.find(({ articleId }) => ProductId === articleId)
      })

      const nonOrderScanItemMapping: ScanItemMapping = nonOrderItemTraces.reduce(
        (mapping: ScanItemMapping, nonOrderItemTrace) => {
          const { ProductId, Created } = nonOrderItemTrace
          if (!mapping[ProductId]) {
            const newManualInputScanItem: ScanItem = {
              orderNo: debouncedSelectedOrderNumber,
              articleId: ProductId,
              quantity: 1,
              lineNo: '',
              traces: [nonOrderItemTrace],
              serialNumbers: [],
              needAttention: [],
              isManualInput: true,
              Created,
            }
            mapping[ProductId] = newManualInputScanItem
            return mapping
          }
          const manualInputScanItem = mapping[ProductId]
          const updatedQuantity = manualInputScanItem.quantity + 1
          const updatedTraces = [...manualInputScanItem.traces, nonOrderItemTrace]
          const updatedManualInputScanItem = {
            ...manualInputScanItem,
            quantity: updatedQuantity,
            traces: updatedTraces,
          }
          mapping[ProductId] = updatedManualInputScanItem
          return mapping
        },
        {},
      )

      return { ...orderScanItemMapping, ...nonOrderScanItemMapping }
    }

    setIsScanItemMappingLoadingInState(true)
    getScanItemMapping()
      .then(scanItemMapping => {
        setIsScanItemMappingLoadingInState(false)
        setScanItemMappingInState(scanItemMapping)
      })
      .catch(() => {
        setIsScanItemMappingLoadingInState(false)
        setScanItemMappingInState({})
      })
  }, [debouncedSelectedOrderNumber, hasGroup, previousDebouncedSelectedOrderNumber, articles])

  const onManualInputSubmit = useCallback(
    ({ articleId, serialNumbers }: ModalFormData) => {
      const scanItem = scanItemMappingInState[articleId]

      Promise.all(serialNumbers.map((serialNumber)=>{
        return batteryChange(serialNumber)
        .catch(() => {
          setFeedbackMessagesInState([FEEDBACK_MESSAGE_ERROR_MAPPING.BATTERY_API_CALL_FAILED])
          return {message : "", serial : ""}
        })
      })).then((results) => {
        const needAttention = results.filter(result => {
          return result.message === 'Needs to change battery'
        }).map(result=> result.serial)

      if (scanItem) {
        const updatedSerialNumbers = [...serialNumbers, ...scanItem.serialNumbers]
        const updatedNeedAttention = [...needAttention, ...scanItem.needAttention]
        const updatedScanItem = { ...scanItem, serialNumbers: updatedSerialNumbers, needAttention: updatedNeedAttention}
        const updatedScanItemMapping = { ...scanItemMappingInState, [articleId]: updatedScanItem }
        setScanItemMappingInState(updatedScanItemMapping)
        setSelectedArticleIdInState(articleId)
        setScanItemMappingInState(updatedScanItemMapping)
      } else {
        const newManualInputScanItem: ScanItem = {
          orderNo: debouncedSelectedOrderNumber,
          articleId,
          quantity: 1,
          lineNo: '',
          traces: [],
          serialNumbers,
          needAttention,
          isManualInput: true,
        }
        const updatedScanItemMapping = { ...scanItemMappingInState, [articleId]: newManualInputScanItem }
        setSelectedArticleIdInState(articleId)
        setScanItemMappingInState(updatedScanItemMapping)
      }
    })
    },
    [debouncedSelectedOrderNumber, scanItemMappingInState],
  )

  useEffect(() => {
    if (scannedTextInState === '') return

    // Text provided by the scanner will include the character "ö" instead of-
    // expected character ";" when user has a Swedish keyboard setting active.
    if (scannedTextInState.includes('ö')) {
      setFeedbackMessagesInState([FEEDBACK_MESSAGE_ERROR_MAPPING.INCLUDES_SWEDISH_LETTERS])
      return
    }

    const EAN_REGEX = /^\d{13}$/
    if (EAN_REGEX.test(scannedTextInState)) {
      const eanArticle = eanArticleMapping[scannedTextInState]
      if (eanArticle) {
        setSelectedArticleIdInState(eanArticle.SKU)
        setScannedTextInState('')
      } else {
        setFeedbackMessagesInState([FEEDBACK_MESSAGE_ERROR_MAPPING.ARTICLE_NOT_FOUND_BY_SCANNED_EAN_NUMBER])
      }
      return
    }

    if (!isDeleteModeInState && !selectedArticleIdInState) {
      setFeedbackMessagesInState([FEEDBACK_MESSAGE_ERROR_MAPPING.EAN_NUMBER_NOT_SCANNED])
      return
    }

    const parsedSerialNumbers = parseSerialNumbers(scannedTextInState)
    if (parsedSerialNumbers.length === 0) return

    // We create a set because we only want to display one of each type of error
    // even though the same error might appear for multiple serial numbers
    const parsedSerialNumberErrorSet: Set<keyof FeedbackMessageErrorMapping> = new Set()

    if (!listHasOnlyUniqueStrings(parsedSerialNumbers)) {
      parsedSerialNumberErrorSet.add(FEEDBACK_MESSAGE_ERROR_MAPPING.DUPLICATE_SERIAL_NUMBER_IN_PARSED_INPUT.name)
    }

    parsedSerialNumbers.forEach(parsedSerialNumber => {
      if (!parsedSerialNumber) {
        parsedSerialNumberErrorSet.add(FEEDBACK_MESSAGE_ERROR_MAPPING.IS_EMPTY_SERIAL_NUMBER.name)
        return
      }
      if (!isValidSerialNumberFormat(parsedSerialNumber)) {
        parsedSerialNumberErrorSet.add(FEEDBACK_MESSAGE_ERROR_MAPPING.INVALID_SERIAL_NUMBER_FORMAT.name)
        return
      }
    })

    if (!isDeleteModeInState && parsedSerialNumberErrorSet.size === 0) {
      const isUniqueAmongstSerialNumbersInState = parsedSerialNumbers.every(parsedSerialNumber => {
        if (!parsedSerialNumber) return true
        const allScanItemSerialNumbers = flatten(
          Object.values(scanItemMappingInState).map(({ serialNumbers }) => serialNumbers),
        )
        return !allScanItemSerialNumbers.includes(parsedSerialNumber)
      })

      if (isUniqueAmongstSerialNumbersInState) {
        onManualInputSubmit({ articleId: selectedArticleIdInState, serialNumbers: parsedSerialNumbers })
      } else {
        parsedSerialNumberErrorSet.add(FEEDBACK_MESSAGE_ERROR_MAPPING.DUPLICATE_SERIAL_NUMBER_IN_STATE.name)
      }
    }

    if (isDeleteModeInState && parsedSerialNumberErrorSet.size === 0) {
      const articleIds = Object.keys(scanItemMappingInState)

      const updatedScanItemMapping = articleIds.reduce((mapping: ScanItemMapping, articleId) => {
        const scanItem = scanItemMappingInState[articleId]
        const { serialNumbers } = scanItem
        if (serialNumbers.length === 0) {
          mapping[articleId] = scanItem
          return mapping
        }
        const remainingSerialNumbers = serialNumbers.filter(serialNumber => {
          return !parsedSerialNumbers.includes(serialNumber)
        })
        const updatedScanItem = { ...scanItem, serialNumbers: remainingSerialNumbers }
        mapping[articleId] = updatedScanItem
        return mapping
      }, {})

      setScanItemMappingInState(updatedScanItemMapping)
    }

    if (parsedSerialNumberErrorSet.size > 0) {
      setFeedbackMessagesInState(
        Array.from(parsedSerialNumberErrorSet).map(
          scanningErrorName => FEEDBACK_MESSAGE_ERROR_MAPPING[scanningErrorName],
        ),
      )
    }

    setScannedTextInState('')
  }, [
    scannedTextInState,
    eanArticleMapping,
    selectedArticleIdInState,
    scanItemMappingInState,
    isDeleteModeInState,
    hasGroup,
    onManualInputSubmit,
  ])

  const scanFormInitialValues: ScanFormValues = {
    orderNumber: '',
    articleId: '',
    traceEvent: '',
    date: null,
    location: locations[0] || '',
    shipTo: '',
    ...(!hasGroup([UserGroup.Franchise]) && {
      reasonCode: REASON_CODES[0],
      specifiedReasonCode: ALL_REASON_CODES[REASON_CODES[0]][0],
    }),
  }

  const validationSchema = scanFormValidationSchema({
    validateOrderNumber: hasGroup(ORDER_NUMBER_ACCESS_GROUPS),
    validateShipTo: hasGroup(SHIP_TO_ACCESS_GROUPS),
  })

  const createScanItemTrace = (articleId: string, serialNumber: string, payload: ScanFormValues): Trace => {
    const { traceEvent, reasonCode, specifiedReasonCode, orderNumber, date, location, shipTo } = payload
    const scanItemTrace: Trace = {
      id: (Math.random() * Date.now()).toString(36).replace('.', ''),
      serialNo: serialNumber,
      TraceEvent: traceEvent.trim(),
      OrderNo: hasGroup(ORDER_NUMBER_ACCESS_GROUPS) ? orderNumber.trim() : 'XX',
      ProductId: articleId,
      TraceTime: date?.toISOString().split('T')[0] || '',
      CompanySupplyChainRole: roles[0] || '',
      UserLocation: location,
      Created: new Date().toISOString(),
    }

    if (reasonCode) scanItemTrace.ReasonCode = reasonCode.trim()
    if (specifiedReasonCode) scanItemTrace.SpecifiedReasonCode = specifiedReasonCode.trim()
    if (companyName) scanItemTrace['Company Name'] = companyName.trim()

    if (hasGroup(SHIP_TO_ACCESS_GROUPS)) scanItemTrace.ShipTo = shipTo

    if (hasGroup([UserGroup.Admin, UserGroup.SuperAdmin, UserGroup.Franchise])) scanItemTrace.ShipTo = 'Customer'

    return scanItemTrace
  }

  const onSubmit = (formValues: ScanFormValues, { setSubmitting }: FormikActions<any>) => {
    const scanItems: ScanItem[] = Object.values(scanItemMappingInState)

    const nestedAllNewScanItemTraces = scanItems.map(scanItem => {
      const { articleId, serialNumbers } = scanItem
      const newScanItemTraces = serialNumbers.map(serialNumber => {
        return createScanItemTrace(articleId, serialNumber, formValues)
      })
      return newScanItemTraces
    })

    const allNewScanItemTraces: Trace[] = flatten(nestedAllNewScanItemTraces)

    return createTraces(allNewScanItemTraces).then(
      () => {
        const updatedScanItemMapping = scanItems.reduce((mapping: ScanItemMapping, scanItem) => {
          const { articleId, traces, serialNumbers, isManualInput } = scanItem
          const newScanItemTraces = allNewScanItemTraces.filter(({ serialNo }) => {
            return serialNumbers.includes(serialNo)
          })
          const updatedScanItemTraces = [...traces, ...newScanItemTraces]
          let updatedScanItem = { ...scanItem, traces: updatedScanItemTraces, serialNumbers: [] }
          if (isManualInput) updatedScanItem = { ...updatedScanItem, Created: new Date().toISOString() }
          mapping[articleId] = updatedScanItem
          return mapping
        }, {})
        setSubmitting(false)
        setScanItemMappingInState(updatedScanItemMapping)
        setFeedbackMessagesInState([FEEDBACK_MESSAGE_SUCCESS_MAPPING.TRACE_SUBMISSION_SUCCESS])
      },
      () => {
        setSubmitting(false)
        setFeedbackMessagesInState([FEEDBACK_MESSAGE_ERROR_MAPPING.TRACE_SUBMISSION_ERROR])
      },
    )
  }

  const onRemoveScanItemSerialNumbers = (articleId: string, serialNumbers: string[]) => {
    const scanItem = scanItemMappingInState[articleId]
    const { serialNumbers: scanItemSerialNumbers, needAttention } = scanItem
    const remainingScanItemSerialNumbers = scanItemSerialNumbers.filter(
      scanItemSerialNumber => !serialNumbers.includes(scanItemSerialNumber),
    )
    const remainingScanItemNeedAttention = needAttention.filter(
      scanNeedAttention => !serialNumbers.includes(scanNeedAttention),
    )
    const updatedScanItem = { ...scanItem, serialNumbers: remainingScanItemSerialNumbers, needAttention: remainingScanItemNeedAttention }
    const updatedScanItemMapping = { ...scanItemMappingInState, [articleId]: updatedScanItem }
    setScanItemMappingInState(updatedScanItemMapping)
  }

  const onRemoveScanItem = (articleId: string) => {
    const { [articleId]: scanItemToRemove, ...remainingScanItems } = scanItemMappingInState
    if (articleId === selectedArticleIdInState) setSelectedArticleIdInState('')
    setScanItemMappingInState(remainingScanItems)
  }

  return (
    <div data-component="scanpage-component">
      <Typography type="h2" css={headerStyles}>
        Scanning
      </Typography>
      <Formik
        initialValues={scanFormInitialValues}
        validationSchema={validationSchema}
        enableReinitialize={true}
        onSubmit={onSubmit}
      >
        {({ values, errors, setFieldValue, isSubmitting, isValid }: FormikProps<any>) => {
          const allScanItemSerialNumbers = flatten(
            Object.values(scanItemMappingInState).map(({ serialNumbers }) => serialNumbers),
          )
          const isSubmitDisabled = isSubmitting || !isValid || !allScanItemSerialNumbers.length
          return (
            <Form css={formStyles}>
              <section css={scanFormPanelStyles}>
                <ScanForm
                  values={values}
                  errors={errors}
                  initialValues={scanFormInitialValues}
                  selectedArticleId={selectedArticleIdInState}
                  selectedOrderNumber={selectedOrderNumberInState}
                  onSetArticleId={setSelectedArticleIdInState}
                  onSetFieldValue={setFieldValue}
                  onSetOrderNumber={setSelectedOrderNumberInState}
                />
              </section>
              <section css={getScanOverviewPanelStyles(isDeleteModeInState)}>
                <ScanOverview
                  isDeleteMode={isDeleteModeInState}
                  isSubmitDisabled={isSubmitDisabled}
                  orderNumber={selectedOrderNumberInState}
                  selectedArticleId={selectedArticleIdInState}
                  scanItemMapping={scanItemMappingInState}
                  isScanItemMappingLoading={isScanItemMappingLoadingInState}
                  feedbackMessages={feedbackMessagesInState}
                  onClearFeedbackMessage={feedbackMessageToDelete => {
                    const remainingFeedbackMessages = feedbackMessagesInState.filter(
                      ({ name }) => name !== feedbackMessageToDelete,
                    )
                    setFeedbackMessagesInState(remainingFeedbackMessages)
                  }}
                  onRemoveScanItem={onRemoveScanItem}
                  onRemoveScanItemSerialNumbers={onRemoveScanItemSerialNumbers}
                  onManualInputSubmit={onManualInputSubmit}
                  onToggleDeleteMode={() => setIsDeleteModeInState(!isDeleteModeInState)}
                />
              </section>
            </Form>
          )
        }}
      </Formik>
    </div>
  )
}

export default Scan
